import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, FileDown, Save } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const TOTALS = new Set(["289", "299", "399", "479", "500", "510"]);
const SECTIONS = [
  ["I. Umsatz", ["200", "205", "220", "221", "225", "230", "235", "280", "289", "299"]],
  ["II. Steuerberechnung", ["302", "303", "312", "313", "342", "343", "321", "331", "381", "382", "399"]],
  ["III. Vorsteuer", ["400", "405", "410", "415", "420", "479"]],
  ["IV. Zahllast", ["500", "510"]],
  ["V. Andere Mittelflüsse", ["900", "910"]],
];

/** Offizielles ESTV-Formular: Ziffern 200–910 mit manuellen Anpassungen + eCH-0217-XML (validiert). */
export function EstvFiguresTable({ companyId, year, period, figures, onChanged }) {
  const [edit, setEdit] = useState({});
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setEdit({ ...(figures?.adjustments || {}) }); setValidation(null); }, [figures]);
  if (!figures?.rows) return null;
  const byKey = Object.fromEntries(figures.rows.map((r) => [r.figure, r]));

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put(`/companies/${companyId}/vat/figure-adjustments`, { figures: edit }, { params: { year, period } });
      toast.success("Ziffern gespeichert – Abrechnung neu berechnet");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); } finally { setBusy(false); }
  };
  const validate = async () => {
    setBusy(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/vat/statement/emwst-xml`, { params: { year, period, validate_only: true } });
      setValidation(r.data);
    } catch (e) { toast.error("Validierung fehlgeschlagen"); } finally { setBusy(false); }
  };
  const download = async () => {
    try {
      const r = await apiClient.get(`/companies/${companyId}/vat/statement/emwst-xml`, { params: { year, period }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/xml" }));
      const a = document.createElement("a"); a.href = url; a.download = `emwst_eCH0217_${year}_${period}.xml`; a.click(); URL.revokeObjectURL(url);
      toast.success(r.headers["x-schema-valid"] === "1" ? "eCH-0217-XML erzeugt (schema-gültig)" : "XML erzeugt – Schema-Warnungen prüfen");
    } catch (e) { toast.error("Download fehlgeschlagen"); }
  };

  return (
    <div className="mt-3 rounded border border-slate-200" data-testid="estv-figures-table">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50">
        <div className="text-xs font-semibold text-slate-800">ESTV-Abrechnungsformular · Ziffern 200–910 {figures.method === "saldo" ? "(Saldosteuersatz)" : "(effektiv)"}</div>
        <div className="flex items-center gap-1.5">
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={validate} disabled={busy} data-testid="estv-validate-button">Schema prüfen</Button>
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={download} data-testid="estv-xml-download-button"><FileDown className="h-3.5 w-3.5 mr-1" />eCH-0217 XML</Button>
          <Button type="button" size="sm" className="h-7 text-xs" onClick={save} disabled={busy} data-testid="estv-save-adjustments-button"><Save className="h-3.5 w-3.5 mr-1" />Anpassungen speichern</Button>
        </div>
      </div>
      {validation && (
        <div className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${validation.valid ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="estv-validation-result">
          {validation.valid ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {validation.valid ? `XML entspricht dem Schema ${validation.schema}` : `Schema-Fehler: ${(validation.errors || []).join(" · ")}`}
          {!validation.uid_ok && <span className="ml-2 text-amber-700">MWST-UID (CHE-…) in den Firmeneinstellungen fehlt – Platzhalter verwendet.</span>}
        </div>
      )}
      <table className="w-full text-[11px]">
        <tbody>
          {SECTIONS.map(([title, keys]) => {
            const rows = keys.filter((k) => byKey[k]);
            if (!rows.length) return null;
            return (
              <React.Fragment key={title}>
                <tr className="bg-slate-50/70"><td colSpan={3} className="px-3 py-1 font-semibold text-slate-600">{title}</td></tr>
                {rows.map((k) => {
                  const r = byKey[k];
                  const total = TOTALS.has(k);
                  return (
                    <tr key={k} className={`border-t border-slate-100 ${total ? "font-semibold bg-slate-50/40" : ""}`} data-testid={`estv-row-${k}`}>
                      <td className="px-3 py-1 w-12 font-mono text-slate-500">{k}</td>
                      <td className="px-2 py-1 text-slate-700">{r.label}{k === "280" && edit["280_text"] !== undefined && (
                        <input className="ml-2 border-b border-slate-300 bg-transparent text-[11px] w-48" placeholder="Bezeichnung" value={edit["280_text"] || ""} onChange={(e) => setEdit({ ...edit, "280_text": e.target.value })} data-testid="estv-input-280-text" />)}</td>
                      <td className="px-3 py-1 text-right font-mono w-36">
                        {r.adjustable ? (
                          <input type="number" step="0.05" className="w-32 text-right border border-slate-200 rounded px-1 py-0.5 text-[11px]" value={edit[k] ?? r.value ?? 0}
                            onChange={(e) => setEdit({ ...edit, [k]: e.target.value, ...(k === "280" && edit["280_text"] === undefined ? { "280_text": "" } : {}) })} data-testid={`estv-input-${k}`} />
                        ) : fmt(r.value)}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default EstvFiguresTable;
